import { useMemo } from 'react'

import { type Control, type FieldPath, type FieldPathValue, type FieldValues, useWatch } from 'react-hook-form'

import {
  agruparParcelasComQuantidade,
  calcularTotalParcelasComJuros,
  type TParcelaComQuantidade,
} from '@/utils/parcelas-com-juros.utils'

type TUseTotalParcelasComJurosProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>
  name: TName
}

type TUseParcelasComJurosProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = TUseTotalParcelasComJurosProps<TFieldValues, TName> & {
  agrupar?: (valorObservado: FieldPathValue<TFieldValues, TName> | undefined) => TParcelaComQuantidade[]
}

const agruparPadrao = (valorObservado: unknown) => {
  if (!Array.isArray(valorObservado)) return []

  return agruparParcelasComQuantidade(valorObservado as Array<{ valor: number | string; quantidade: number }>)
}

export const useParcelasComJuros = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  agrupar = agruparPadrao as (
    valorObservado: FieldPathValue<TFieldValues, TName> | undefined,
  ) => TParcelaComQuantidade[],
}: TUseParcelasComJurosProps<TFieldValues, TName>) => {
  const valorObservado = useWatch({ control, name })

  const parcelasAgrupadas = useMemo(() => agrupar(valorObservado), [agrupar, valorObservado])

  const totalComJuros = useMemo(() => calcularTotalParcelasComJuros(parcelasAgrupadas), [parcelasAgrupadas])

  return {
    valorObservado,
    parcelasAgrupadas,
    totalComJuros,
  }
}
